import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Select,
    FormControl,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    TextField,
} from "@mui/material";
import {
    ExpandMore,
    Person,
    Business,
    AccountBalance,
    CheckCircle,
    RadioButtonUnchecked,
    ArrowBack,
    AccountTree,
    Link as LinkIcon,
    LinkOff,
    Undo,
    DragIndicator,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";

import { GroupTask, MatchFormsData, PageGrouping, PARENT_FORM_TYPES, FORM_ASSIGNMENT_RULES } from "../../interface/returns";
import { Loan } from "../../interface/loan";
import { AppDispatch, RootState } from "../../configureStore";
import { SET_HAS_CHANGES, saveMatchForms, kickbackTask } from "../../redux/reducer/returns";
import { normalizeTins, normalizeNameTokens } from "../../utils";

interface FormEntityAssignerParams {
    groupTask: GroupTask;
    activeLoan?: Loan;
    matchData: MatchFormsData;
    setMatchData: (data: MatchFormsData) => void;
    onPageChange: (page: number) => void;
    onBack: () => void;
}

interface EntityInfo {
    type: 'borrower' | 'business' | 'trust';
    id: string;
    name: string;
    tin?: string;
}

const ENTITY_ICONS: Record<string, typeof Person> = {
    borrower: Person,
    business: Business,
    trust: AccountBalance,
};

const ENTITY_COLORS: Record<string, string> = {
    borrower: 'primary',
    business: 'secondary',
    trust: 'info',
};

const FormEntityAssigner = ({
    groupTask,
    activeLoan,
    matchData,
    setMatchData,
    onPageChange,
    onBack,
}: FormEntityAssignerParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const isSaving = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['returns/SAVE_MATCH_FORMS']);
    const [expandedTaxYear, setExpandedTaxYear] = useState<string | false>(false);
    const [linkMenuAnchor, setLinkMenuAnchor] = useState<{ element: HTMLElement; formId: string } | null>(null);
    const [undoHistory, setUndoHistory] = useState<Array<{
        type: 'assign' | 'unassign' | 'link' | 'unlink';
        formId: string;
        data: any;
        timestamp: number;
    }>>([]);
    const [draggedFormId, setDraggedFormId] = useState<string | null>(null);
    const [dragOverParentId, setDragOverParentId] = useState<string | null>(null);
    const [highlightedFormId, setHighlightedFormId] = useState<string | null>(null);
    const [showUnassignedDialog, setShowUnassignedDialog] = useState(false);
    const [showKickbackDialog, setShowKickbackDialog] = useState(false);
    const [kickbackReason, setKickbackReason] = useState("");
    const [kickbackSubmitting, setKickbackSubmitting] = useState(false);

    // Build flat list of all entities
    const allEntities = useMemo((): EntityInfo[] => {
        const borrowers = (activeLoan?.borrowers || []).map((b) => ({ type: 'borrower' as const, id: b.irsIncomeId, name: b.name, tin: b.tin }));
        const businesses = (activeLoan?.businesses || []).map((b) => ({ type: 'business' as const, id: b.irsIncomeId, name: b.name, tin: b.tin }));
        const trusts = (activeLoan?.trusts || []).map((t) => ({ type: 'trust' as const, id: t.irsIncomeId, name: t.name, tin: t.tin }));
        return [...borrowers, ...businesses, ...trusts];
    }, [activeLoan]);

    // Sort forms with PARENT_FORM_TYPES priority
    const sortForms = useCallback((a: PageGrouping, b: PageGrouping): number => {
        const aIsParent = PARENT_FORM_TYPES.includes(a.formType as any);
        const bIsParent = PARENT_FORM_TYPES.includes(b.formType as any);

        // If both are parent types, sort by PARENT_FORM_TYPES order
        if (aIsParent && bIsParent) {
            const aIndex = PARENT_FORM_TYPES.indexOf(a.formType as any);
            const bIndex = PARENT_FORM_TYPES.indexOf(b.formType as any);
            return aIndex - bIndex;
        }

        // If only one is a parent type, it comes first
        if (aIsParent) return -1;
        if (bIsParent) return 1;

        // If neither is a parent type, sort alphabetically
        return a.formType.localeCompare(b.formType);
    }, []);

    // Get groups by tax year
    const getGroupsForTaxYear = useCallback((taxYear: string): PageGrouping[] => Object.values(matchData.pageGroups).filter(
        (group) => group.taxYear === taxYear,
    ).sort(sortForms), [matchData.pageGroups, sortForms]);

    // Get tax years
    const taxYears = useMemo(() => {
        const years = new Set(Object.values(matchData.pageGroups).map((g) => g.taxYear));
        return Array.from(years).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
    }, [matchData.pageGroups]);

    // Assign form to entity
    const handleAssignForm = useCallback((formInstanceId: string, entityType: 'borrower' | 'business' | 'trust', entityId: string, entityName: string) => {
        const group = matchData.pageGroups[formInstanceId];

        // If this is a parent form being assigned, sweep for orphaned children
        // in the same tax year already assigned to the same entity and link them.
        const formRule = group ? FORM_ASSIGNMENT_RULES[group.formType] : undefined;
        const isParentForm = !!group
            && (formRule?.canBeParent !== false)
            && PARENT_FORM_TYPES.includes(group.formType as any);

        const orphanChildIds: string[] = [];
        if (isParentForm && group) {
            Object.values(matchData.pageGroups).forEach((g) => {
                if (g.formInstanceId === formInstanceId) return;
                if (g.taxYear !== group.taxYear) return;
                const childRule = FORM_ASSIGNMENT_RULES[g.formType];
                const childCanHaveParent = !childRule || childRule.canHaveParent !== false;
                const childIsParentType = PARENT_FORM_TYPES.includes(g.formType as any)
                    && childRule?.canBeParent !== false;
                if (!childCanHaveParent || childIsParentType) return;
                const childAssignment = matchData.formAssignments[g.formInstanceId];
                if (!childAssignment?.assignedTo) return;
                if (childAssignment.assignedTo.irsIncomeId !== entityId) return;
                if (childAssignment.parentFormId) return;
                orphanChildIds.push(g.formInstanceId);
            });
        }

        // Record previous state for undo (including any orphan links we add)
        const previousAssignment = matchData.formAssignments[formInstanceId]?.assignedTo;
        setUndoHistory((prev) => [
            ...prev.slice(-49), // Keep max 50 items
            {
                type: previousAssignment ? 'assign' : 'unassign',
                formId: formInstanceId,
                data: { previousAssignment, autoLinkedOrphans: orphanChildIds },
                timestamp: Date.now(),
            },
        ]);

        const newAssignments = { ...matchData.formAssignments };
        const existingChildIds = newAssignments[formInstanceId]?.childFormIds || [];
        newAssignments[formInstanceId] = {
            ...newAssignments[formInstanceId],
            formInstanceId,
            assignedTo: { type: entityType, irsIncomeId: entityId, name: entityName },
            manualOverride: true,
            ...(orphanChildIds.length > 0
                ? { childFormIds: [...existingChildIds, ...orphanChildIds] }
                : {}),
        };
        for (const orphanId of orphanChildIds) {
            newAssignments[orphanId] = {
                ...newAssignments[orphanId],
                parentFormId: formInstanceId,
            };
        }

        setMatchData({
            ...matchData,
            formAssignments: newAssignments,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });

        // Highlight the just-assigned form
        setHighlightedFormId(formInstanceId);

        // Navigate to the next unassigned form in the same tax year
        if (group) {
            const allUnassigned = Object.values(matchData.pageGroups)
                .filter((g) => g.taxYear === group.taxYear
                    && !matchData.formAssignments[g.formInstanceId]?.assignedTo
                    && !matchData.formAssignments[g.formInstanceId]?.parentFormId)
                .sort(sortForms);
            const idx = allUnassigned.findIndex((g) => g.formInstanceId === formInstanceId);
            const remaining = allUnassigned.filter((g) => g.formInstanceId !== formInstanceId);
            if (remaining.length > 0) {
                const nextIdx = idx >= remaining.length ? remaining.length - 1 : idx;
                const nextForm = remaining[nextIdx];
                if (nextForm.pages.length > 0) {
                    onPageChange(nextForm.pages[0].page);
                }
            }
        }
    }, [matchData, setMatchData, dispatch, sortForms, onPageChange]);

    // Assign form as child to parent form
    const handleAssignFormToParent = useCallback((childFormId: string, parentFormId: string) => {
        // Record previous state for undo
        const previousParentFormId = matchData.formAssignments[childFormId]?.parentFormId;
        const previousAssignedTo = matchData.formAssignments[childFormId]?.assignedTo;
        setUndoHistory((prev) => [
            ...prev.slice(-49), // Keep max 50 items
            {
                type: 'link',
                formId: childFormId,
                data: { previousParentFormId, previousAssignedTo, newParentFormId: parentFormId },
                timestamp: Date.now(),
            },
        ]);

        const newAssignments = { ...matchData.formAssignments };

        // Initialize parent if it doesn't exist
        if (!newAssignments[parentFormId]) {
            newAssignments[parentFormId] = {
                formInstanceId: parentFormId,
                childFormIds: [],
            };
        }

        // Initialize child if it doesn't exist
        if (!newAssignments[childFormId]) {
            newAssignments[childFormId] = {
                formInstanceId: childFormId,
            };
        }

        // Remove child from any previous parent
        Object.keys(newAssignments).forEach((assignmentId) => {
            if (newAssignments[assignmentId].childFormIds?.includes(childFormId)) {
                newAssignments[assignmentId] = {
                    ...newAssignments[assignmentId],
                    childFormIds: newAssignments[assignmentId].childFormIds?.filter((id) => id !== childFormId),
                };
            }
        });

        // Add child to new parent
        if (!newAssignments[parentFormId].childFormIds) {
            newAssignments[parentFormId].childFormIds = [];
        }
        if (!newAssignments[parentFormId].childFormIds.includes(childFormId)) {
            newAssignments[parentFormId].childFormIds.push(childFormId);
        }

        // Set parent reference on child
        newAssignments[childFormId].parentFormId = parentFormId;
        newAssignments[childFormId].manualOverride = true;

        // If parent has an entity assignment, inherit it for the child
        if (newAssignments[parentFormId].assignedTo) {
            newAssignments[childFormId].assignedTo = newAssignments[parentFormId].assignedTo;
        }

        setMatchData({
            ...matchData,
            formAssignments: newAssignments,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
    }, [matchData, setMatchData, dispatch]);

    // Unassign form from parent
    const handleUnassignFormFromParent = useCallback((childFormId: string) => {
        // Record previous state for undo
        const previousParentFormId = matchData.formAssignments[childFormId]?.parentFormId;
        const previousAssignedTo = matchData.formAssignments[childFormId]?.assignedTo;
        if (previousParentFormId) {
            setUndoHistory((prev) => [
                ...prev.slice(-49), // Keep max 50 items
                {
                    type: 'unlink',
                    formId: childFormId,
                    data: { previousParentFormId, previousAssignedTo },
                    timestamp: Date.now(),
                },
            ]);
        }

        const newAssignments = { ...matchData.formAssignments };
        const childAssignment = newAssignments[childFormId];

        if (childAssignment?.parentFormId) {
            const parentId = childAssignment.parentFormId;

            // Remove from parent's children
            if (newAssignments[parentId]?.childFormIds) {
                newAssignments[parentId].childFormIds = newAssignments[parentId].childFormIds.filter((id) => id !== childFormId);
            }

            // Remove parent reference from child
            delete childAssignment.parentFormId;
            childAssignment.manualOverride = true;
        }

        setMatchData({
            ...matchData,
            formAssignments: newAssignments,
        });
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
    }, [matchData, setMatchData, dispatch]);

    // Undo last action
    const handleUndo = useCallback(() => {
        if (undoHistory.length === 0) return;

        const lastAction = undoHistory[undoHistory.length - 1];
        const newAssignments = { ...matchData.formAssignments };

        switch (lastAction.type) {
            case 'assign':
            case 'unassign': {
                // Restore previous assignment
                if (lastAction.data.previousAssignment) {
                    newAssignments[lastAction.formId] = {
                        ...newAssignments[lastAction.formId],
                        assignedTo: lastAction.data.previousAssignment,
                        manualOverride: true,
                    };
                } else if (newAssignments[lastAction.formId]) {
                    // Was unassigned before, remove assignment
                    delete newAssignments[lastAction.formId].assignedTo;
                    newAssignments[lastAction.formId].manualOverride = true;
                }
                // Unwind any orphan children that were auto-linked at assign time
                const orphans: string[] = lastAction.data.autoLinkedOrphans || [];
                if (orphans.length > 0) {
                    for (const orphanId of orphans) {
                        if (newAssignments[orphanId]) {
                            delete newAssignments[orphanId].parentFormId;
                        }
                    }
                    if (newAssignments[lastAction.formId]?.childFormIds) {
                        newAssignments[lastAction.formId].childFormIds = newAssignments[lastAction.formId]
                            .childFormIds!.filter((id) => !orphans.includes(id));
                    }
                }
                break;
            }

            case 'link': {
                // Undo parent-child link
                if (lastAction.data.previousParentFormId) {
                    // Restore to previous parent
                    const prevParentId = lastAction.data.previousParentFormId;
                    if (!newAssignments[prevParentId].childFormIds) {
                        newAssignments[prevParentId].childFormIds = [];
                    }
                    if (!newAssignments[prevParentId].childFormIds.includes(lastAction.formId)) {
                        newAssignments[prevParentId].childFormIds.push(lastAction.formId);
                    }
                    newAssignments[lastAction.formId].parentFormId = prevParentId;
                } else {
                    // Was unlinked before, remove from current parent
                    const currentParentId = lastAction.data.newParentFormId;
                    if (newAssignments[currentParentId]?.childFormIds) {
                        newAssignments[currentParentId].childFormIds = newAssignments[currentParentId].childFormIds.filter(
                            (id) => id !== lastAction.formId,
                        );
                    }
                    if (newAssignments[lastAction.formId]) {
                        delete newAssignments[lastAction.formId].parentFormId;
                    }
                }
                // Restore previous assignment
                if (lastAction.data.previousAssignedTo) {
                    newAssignments[lastAction.formId].assignedTo = lastAction.data.previousAssignedTo;
                }
                newAssignments[lastAction.formId].manualOverride = true;
                break;
            }

            case 'unlink': {
                // Undo unlink - restore parent
                const parentId = lastAction.data.previousParentFormId;
                if (!newAssignments[parentId].childFormIds) {
                    newAssignments[parentId].childFormIds = [];
                }
                if (!newAssignments[parentId].childFormIds.includes(lastAction.formId)) {
                    newAssignments[parentId].childFormIds.push(lastAction.formId);
                }
                newAssignments[lastAction.formId].parentFormId = parentId;
                if (lastAction.data.previousAssignedTo) {
                    newAssignments[lastAction.formId].assignedTo = lastAction.data.previousAssignedTo;
                }
                newAssignments[lastAction.formId].manualOverride = true;
                break;
            }

            default:
                break;
        }

        setMatchData({
            ...matchData,
            formAssignments: newAssignments,
        });
        setUndoHistory((prev) => prev.slice(0, -1));
        dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
    }, [undoHistory, matchData, setMatchData, dispatch]);

    // Drag & drop handlers
    const handleDragStart = useCallback((e: React.DragEvent, formId: string) => {
        e.stopPropagation();
        setDraggedFormId(formId);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent, parentFormId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (!draggedFormId) return;

        // Check if this is a valid drop target
        const potentialParents = getPotentialParents(draggedFormId);
        if (potentialParents.find((p) => p.formInstanceId === parentFormId)) {
            e.dataTransfer.dropEffect = 'move';
            setDragOverParentId(parentFormId);
        } else {
            e.dataTransfer.dropEffect = 'none';
        }
    }, [draggedFormId, matchData]);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.stopPropagation();
        setDragOverParentId(null);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent, parentFormId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggedFormId && draggedFormId !== parentFormId) {
            // Check if valid drop
            const potentialParents = getPotentialParents(draggedFormId);
            if (potentialParents.find((p) => p.formInstanceId === parentFormId)) {
                handleAssignFormToParent(draggedFormId, parentFormId);
            }
        }
        setDraggedFormId(null);
        setDragOverParentId(null);
    }, [draggedFormId, handleAssignFormToParent, matchData]);

    const handleDragEnd = useCallback(() => {
        setDraggedFormId(null);
        setDragOverParentId(null);
    }, []);

    // Get form assignment status
    const getFormStatus = useCallback((formInstanceId: string): 'assigned' | 'unassigned' => {
        const assignment = matchData.formAssignments[formInstanceId];
        return assignment?.assignedTo ? 'assigned' : 'unassigned';
    }, [matchData.formAssignments]);

    // Get assignment rules for a form type
    const getFormAssignmentRule = useCallback((formType: string) => FORM_ASSIGNMENT_RULES[formType], []);

    // Check if a form type can be a parent
    const canBeParent = useCallback((formType: string): boolean => {
        // First check if there's a specific rule that disallows being a parent
        const rule = getFormAssignmentRule(formType);
        if (rule && !rule.canBeParent) return false;

        // Otherwise check if it's in the PARENT_FORM_TYPES list
        return PARENT_FORM_TYPES.includes(formType as any);
    }, [getFormAssignmentRule]);

    // Check if a form type can have a parent
    const canHaveParent = useCallback((formType: string): boolean => {
        const rule = getFormAssignmentRule(formType);
        // If there's a rule that says it can't have a parent, return false
        if (rule && !rule.canHaveParent) return false;
        // By default, non-parent forms can have parents
        return true;
    }, [getFormAssignmentRule]);

    // Get allowed entity types for a form
    const getAllowedEntityTypes = useCallback((formType: string): ('borrower' | 'business' | 'trust')[] => {
        const rule = getFormAssignmentRule(formType);
        // If there's a specific rule, use it; otherwise allow all
        return rule?.allowedEntityTypes || ['borrower', 'business', 'trust'];
    }, [getFormAssignmentRule]);

    // Auto-assign forms when there's only one eligible entity, or via TIN matching
    useEffect(() => {
        if (!activeLoan) return;

        const borrowers = activeLoan.borrowers || [];
        const businesses = activeLoan.businesses || [];
        const trusts = activeLoan.trusts || [];

        const newAssignments = { ...matchData.formAssignments };
        let hasChanges = false;

        // Pass 1: Single-eligible-entity auto-assign
        Object.values(matchData.pageGroups).forEach((group) => {
            // Skip already assigned forms
            if (newAssignments[group.formInstanceId]?.assignedTo) return;
            // Skip forms the user has explicitly touched
            if (newAssignments[group.formInstanceId]?.manualOverride) return;

            const allowedTypes = getAllowedEntityTypes(group.formType);
            const eligible: Array<{ type: 'borrower' | 'business' | 'trust'; id: string; name: string }> = [];

            if (allowedTypes.includes('borrower')) {
                borrowers.forEach((b) => eligible.push({ type: 'borrower', id: b.irsIncomeId, name: b.name }));
            }
            if (allowedTypes.includes('business')) {
                businesses.forEach((b) => eligible.push({ type: 'business', id: b.irsIncomeId, name: b.name }));
            }
            if (allowedTypes.includes('trust')) {
                trusts.forEach((t) => eligible.push({ type: 'trust', id: t.irsIncomeId, name: t.name }));
            }

            if (eligible.length === 1) {
                const entity = eligible[0];
                newAssignments[group.formInstanceId] = {
                    ...newAssignments[group.formInstanceId],
                    formInstanceId: group.formInstanceId,
                    assignedTo: {
                        type: entity.type,
                        irsIncomeId: entity.id,
                        name: entity.name,
                    },
                };
                hasChanges = true;
            }
        });

        // Pass 2: TIN-based auto-assign for forms with multiple eligible entities
        // Build set of normalized schedule_c TINs (forms that could be parents for ambiguous children)
        const scheduleCTins = new Set<string>();
        Object.values(matchData.pageGroups).forEach((group) => {
            if (group.formType === 'schedule_c' && group.tin) {
                const normalized = normalizeTins(group.tin);
                if (normalized.length === 9) {
                    scheduleCTins.add(normalized);
                }
            }
        });

        Object.values(matchData.pageGroups).forEach((group) => {
            // Skip already assigned forms (including those just assigned in pass 1)
            if (newAssignments[group.formInstanceId]?.assignedTo) return;
            // Skip forms the user has explicitly touched
            if (newAssignments[group.formInstanceId]?.manualOverride) return;

            // Skip forms without a TIN
            const formTin = normalizeTins(group.tin);
            if (formTin.length !== 9) return;

            // Skip potential schedule_c children when a schedule_c with the same TIN exists
            // (user must decide if these belong under the individual or the schedule_c parent)
            if (canHaveParent(group.formType) && !canBeParent(group.formType) && (scheduleCTins.has(formTin) && group.formType !== 'schedule_1')) {
                return;
            }

            const allowedTypes = getAllowedEntityTypes(group.formType);
            const eligible = allEntities.filter((e) => allowedTypes.includes(e.type));

            // Only use TIN matching when there are multiple eligible entities
            if (eligible.length <= 1) return;

            // Find entities whose TIN matches the form's TIN
            const tinMatches = eligible.filter((e) => {
                const entityTin = normalizeTins(e.tin);
                return entityTin.length === 9 && entityTin === formTin;
            });

            // Auto-assign only if exactly one entity matches
            if (tinMatches.length === 1) {
                const entity = tinMatches[0];
                newAssignments[group.formInstanceId] = {
                    ...newAssignments[group.formInstanceId],
                    formInstanceId: group.formInstanceId,
                    assignedTo: {
                        type: entity.type,
                        irsIncomeId: entity.id,
                        name: entity.name,
                    },
                };
                hasChanges = true;
            }
        });

        // Pass 3: Name-based fuzzy match for forms with multiple eligible entities
        // and a usable name field. An entity matches when all of its (≥2) tokens
        // appear in the form's tokens; auto-assign only when exactly one entity
        // matches (so joint returns and ambiguous cases fall through to manual).
        Object.values(matchData.pageGroups).forEach((group) => {
            if (newAssignments[group.formInstanceId]?.assignedTo) return;
            if (newAssignments[group.formInstanceId]?.manualOverride) return;

            const formTokens = new Set(normalizeNameTokens(group.name));
            if (formTokens.size === 0) return;

            const allowedTypes = getAllowedEntityTypes(group.formType);
            const eligible = allEntities.filter((e) => allowedTypes.includes(e.type));
            if (eligible.length <= 1) return;

            const nameMatches = eligible.filter((e) => {
                const entityTokens = normalizeNameTokens(e.name);
                if (entityTokens.length < 2) return false;
                return entityTokens.every((t) => formTokens.has(t));
            });

            if (nameMatches.length === 1) {
                const entity = nameMatches[0];
                newAssignments[group.formInstanceId] = {
                    ...newAssignments[group.formInstanceId],
                    formInstanceId: group.formInstanceId,
                    assignedTo: {
                        type: entity.type,
                        irsIncomeId: entity.id,
                        name: entity.name,
                    },
                };
                hasChanges = true;
            }
        });

        if (hasChanges) {
            setMatchData({
                ...matchData,
                formAssignments: newAssignments,
            });
            dispatch({ type: SET_HAS_CHANGES, hasChanges: true });
        }
    }, [matchData, activeLoan, getAllowedEntityTypes, allEntities, canHaveParent, canBeParent, setMatchData, dispatch]);

    // Get potential parent forms for a child form (same tax year, can be parent, not already a child itself, and must be assigned to an entity)
    const getPotentialParents = useCallback((childFormId: string): PageGrouping[] => {
        const childGroup = matchData.pageGroups[childFormId];
        if (!childGroup) return [];

        // Check if this form type is allowed to have a parent
        if (!canHaveParent(childGroup.formType)) return [];

        // Child forms can't themselves be potential parents
        if (canBeParent(childGroup.formType)) return [];

        return Object.values(matchData.pageGroups).filter((group) => group.formInstanceId !== childFormId
            && group.taxYear === childGroup.taxYear
            && canBeParent(group.formType)
            && !matchData.formAssignments[group.formInstanceId]?.parentFormId
            && matchData.formAssignments[group.formInstanceId]?.assignedTo); // Must be assigned to an entity
    }, [matchData.pageGroups, matchData.formAssignments, canBeParent, canHaveParent]);

    // Save as draft
    const handleSaveDraft = useCallback(() => {
        if (!groupTask?.key) return;

        dispatch(saveMatchForms({
            input: {
                matchData,
                key: groupTask.key,
                draft: true,
            },
            onSuccess: () => {
                dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
                navigate('/returns');
            },
        }));
    }, [matchData, groupTask, dispatch, navigate]);

    // Count unassigned forms
    const unassignedCount = useMemo(() => Object.values(matchData.pageGroups).filter(
        (group) => !matchData.formAssignments[group.formInstanceId]?.assignedTo,
    ).length, [matchData.pageGroups, matchData.formAssignments]);

    // Submit final (called after confirmation if needed)
    const doSubmit = useCallback(() => {
        if (!groupTask?.key) return;

        dispatch(saveMatchForms({
            input: {
                matchData,
                key: groupTask.key,
                draft: false,
            },
            onSuccess: () => {
                dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
                navigate('/returns');
            },
        }));
    }, [matchData, groupTask, dispatch, navigate]);

    // Submit with unassigned check
    const handleSubmit = useCallback(() => {
        if (unassignedCount > 0) {
            setShowUnassignedDialog(true);
        } else {
            doSubmit();
        }
    }, [unassignedCount, doSubmit]);

    const handleKickbackClick = useCallback(() => {
        setKickbackReason("");
        setShowKickbackDialog(true);
    }, []);

    const handleKickbackConfirm = useCallback(() => {
        if (!groupTask?.key || !kickbackReason.trim() || kickbackSubmitting) return;
        setKickbackSubmitting(true);
        dispatch(kickbackTask({
            input: { key: groupTask.key, reason: kickbackReason.trim() },
            onSuccess: () => {
                dispatch({ type: SET_HAS_CHANGES, hasChanges: false });
                setShowKickbackDialog(false);
                setKickbackSubmitting(false);
                navigate('/returns');
            },
            onError: () => {
                setKickbackSubmitting(false);
            },
        }));
    }, [groupTask, kickbackReason, kickbackSubmitting, dispatch, navigate]);

    // Get eligible entities for a form's inline Select
    const getEligibleEntities = useCallback((formType: string): EntityInfo[] => {
        const allowedTypes = getAllowedEntityTypes(formType);
        return allEntities.filter((e) => allowedTypes.includes(e.type));
    }, [getAllowedEntityTypes, allEntities]);

    // Handle inline Select assignment. Value is prefixed: "parent:<id>" links
    // to a parent form (which inherits assignedTo); "entity:<id>" assigns
    // directly to an entity at the top level.
    const handleSelectAssign = useCallback((formInstanceId: string, value: string) => {
        if (!value) return;
        if (value.startsWith('parent:')) {
            const parentFormId = value.slice('parent:'.length);
            handleAssignFormToParent(formInstanceId, parentFormId);
            return;
        }
        if (value.startsWith('entity:')) {
            const entityId = value.slice('entity:'.length);
            const entity = allEntities.find((e) => e.id === entityId);
            if (!entity) return;
            handleAssignForm(formInstanceId, entity.type, entity.id, entity.name);
        }
    }, [allEntities, handleAssignForm, handleAssignFormToParent]);

    // Render form item — simplified two-line layout
    const renderFormItem = (group: PageGrouping, showAssignDropdown: boolean = false) => {
        const status = getFormStatus(group.formInstanceId);
        const assignment = matchData.formAssignments[group.formInstanceId];
        const hasParent = !!assignment?.parentFormId;
        const hasChildren = (assignment?.childFormIds?.length || 0) > 0;
        const parentGroup = hasParent && assignment?.parentFormId
            ? matchData.pageGroups[assignment.parentFormId]
            : null;
        const isParentForm = canBeParent(group.formType);
        const potentialParents = getPotentialParents(group.formInstanceId);
        const canBeDragged = !hasParent && potentialParents.length > 0;
        const isDropTarget = dragOverParentId === group.formInstanceId;
        const isBeingDragged = draggedFormId === group.formInstanceId;
        const isHighlighted = highlightedFormId === group.formInstanceId;

        return (
            <Box key={group.formInstanceId}>
                <Box
                    draggable={canBeDragged}
                    onDragStart={(e) => canBeDragged && handleDragStart(e, group.formInstanceId)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => isParentForm && handleDragOver(e, group.formInstanceId)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => isParentForm && handleDrop(e, group.formInstanceId)}
                    onClick={() => {
                        if (group.pages.length > 0) {
                            onPageChange(group.pages[0].page);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: isHighlighted ? 'action.selected' : isDropTarget ? 'success.light' : 'background.paper',
                        borderRadius: 1,
                        border: 1,
                        borderColor: isHighlighted ? 'success.main' : isDropTarget ? 'success.main' : isBeingDragged ? 'primary.main' : 'divider',
                        borderStyle: isDropTarget ? 'dashed' : 'solid',
                        borderLeftWidth: isHighlighted ? 3 : 1,
                        borderLeftColor: isHighlighted ? 'success.main' : status === 'assigned' ? 'success.main' : 'divider',
                        mb: 0.5,
                        cursor: group.pages.length > 0 ? 'pointer' : 'default',
                        opacity: isBeingDragged ? 0.5 : 1,
                        transition: 'all 0.2s',
                        '&:hover': group.pages.length > 0 ? {
                            bgcolor: isDropTarget ? 'success.light' : 'action.hover',
                            borderColor: isDropTarget ? 'success.main' : 'primary.main',
                        } : {},
                    }}
                >
                    {canBeDragged && (
                        <DragIndicator
                            fontSize="small"
                            sx={{ cursor: 'grab', color: 'text.secondary', '&:active': { cursor: 'grabbing' } }}
                        />
                    )}
                    {status === 'assigned' ? (
                        <CheckCircle sx={{ fontSize: 16 }} color="success" />
                    ) : (
                        <RadioButtonUnchecked sx={{ fontSize: 16 }} color="disabled" />
                    )}

                    {/* Two-line content */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                            {group.formType}
                            {group.name && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                                    {group.name}
                                </Typography>
                            )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {assignment?.assignedTo && assignment.assignedTo.name}
                            {hasParent && parentGroup && (
                                <Chip
                                    label={`Child of ${parentGroup.formType}${parentGroup.name ? ` ${parentGroup.name}` : ''}`}
                                    size="small"
                                    icon={<AccountTree />}
                                    sx={{ ml: 0.5, height: 18, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }}
                                    onDelete={(e) => {
                                        e.stopPropagation();
                                        handleUnassignFormFromParent(group.formInstanceId);
                                    }}
                                    deleteIcon={<LinkOff sx={{ fontSize: 12 }} />}
                                />
                            )}
                            {hasChildren && (
                                <Chip
                                    label={`${assignment.childFormIds?.length} children`}
                                    size="small"
                                    icon={<AccountTree />}
                                    color="primary"
                                    sx={{ ml: 0.5, height: 18, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }}
                                />
                            )}
                        </Typography>
                    </Box>

                    {/* Inline assign dropdown for unassigned forms.
                        Parent-link options come first when available, so the
                        user's reflexive "Assign to..." action surfaces parent
                        linking instead of bypassing it. */}
                    {showAssignDropdown && status === 'unassigned' && !hasParent && (
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <Select
                                value=""
                                displayEmpty
                                renderValue={() => "Assign to..."}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectAssign(group.formInstanceId, e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ fontSize: '0.8rem', height: 32 }}
                            >
                                {potentialParents.map((parentForm) => {
                                    const parentEntity = matchData.formAssignments[parentForm.formInstanceId]?.assignedTo;
                                    const ownerName = parentEntity?.name ?? '';
                                    const primary = parentForm.name
                                        ? `Link to ${parentForm.name}`
                                        : `Link to ${ownerName}'s ${parentForm.formType}`;
                                    const secondary = parentForm.name
                                        ? [parentForm.formType, ownerName].filter(Boolean).join(' · ')
                                        : undefined;
                                    return (
                                        <MenuItem key={`parent:${parentForm.formInstanceId}`} value={`parent:${parentForm.formInstanceId}`}>
                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                <AccountTree fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={primary}
                                                secondary={secondary}
                                                slotProps={{ primary: { variant: 'body2' } }}
                                            />
                                        </MenuItem>
                                    );
                                })}
                                {potentialParents.length > 0 && <Divider />}
                                {getEligibleEntities(group.formType).map((entity) => {
                                    const Icon = ENTITY_ICONS[entity.type];
                                    return (
                                        <MenuItem key={`entity:${entity.id}`} value={`entity:${entity.id}`}>
                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                <Icon fontSize="small" color={ENTITY_COLORS[entity.type] as any} />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={entity.name}
                                                secondary={potentialParents.length > 0 ? 'Direct (no parent)' : undefined}
                                                slotProps={{ primary: { variant: 'body2' } }}
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                        </FormControl>
                    )}

                    {/* Parent link icon button — only for already-assigned forms.
                        Unassigned forms get the parent options inline in the
                        dropdown above. */}
                    {!hasParent && potentialParents.length > 0 && !showAssignDropdown && (
                        <Tooltip title="Link to parent form">
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setLinkMenuAnchor({ element: e.currentTarget as HTMLElement, formId: group.formInstanceId });
                                }}
                            >
                                <LinkIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                {hasChildren && assignment.childFormIds && (
                    <Box sx={{ ml: 4, pl: 2, borderLeft: 2, borderColor: 'primary.main' }}>
                        {assignment.childFormIds.map((childId) => {
                            const childGroup = matchData.pageGroups[childId];
                            return childGroup ? renderFormItem(childGroup) : null;
                        })}
                    </Box>
                )}
            </Box>
        );
    };

    // Render flat entity section header + its forms
    const renderEntitySection = (taxYear: string, entity: EntityInfo) => {
        const Icon = ENTITY_ICONS[entity.type];
        const formCount = Object.values(matchData.pageGroups).filter(
            (group) => group.taxYear === taxYear
                && matchData.formAssignments[group.formInstanceId]?.assignedTo?.irsIncomeId === entity.id
                && !matchData.formAssignments[group.formInstanceId]?.parentFormId,
        ).length;

        const entityForms = Object.values(matchData.pageGroups).filter(
            (group) => group.taxYear === taxYear
                && matchData.formAssignments[group.formInstanceId]?.assignedTo?.irsIncomeId === entity.id
                && !matchData.formAssignments[group.formInstanceId]?.parentFormId,
        ).sort(sortForms);

        return (
            <Box key={entity.id} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Icon fontSize="small" color={ENTITY_COLORS[entity.type] as any} />
                    <Typography variant="subtitle2" sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {entity.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {formCount} form{formCount !== 1 ? 's' : ''}
                    </Typography>
                </Box>
                {entityForms.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5 }}>
                        No forms assigned
                    </Typography>
                ) : (
                    entityForms.map((group) => renderFormItem(group))
                )}
            </Box>
        );
    };

    // Render unassigned section with inline Select dropdowns
    const renderUnassignedSection = (taxYear: string) => {
        const unassignedForms = Object.values(matchData.pageGroups).filter(
            (group) => group.taxYear === taxYear
                && !matchData.formAssignments[group.formInstanceId]?.assignedTo
                && !matchData.formAssignments[group.formInstanceId]?.parentFormId,
        ).sort(sortForms);

        if (unassignedForms.length === 0) {
            return (
                <Box key="unassigned" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Unassigned
                    </Typography>
                    <Typography variant="caption" color="success.main" sx={{ ml: 0.5 }}>
                        All forms assigned!
                    </Typography>
                </Box>
            );
        }

        return (
            <Box key="unassigned" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <RadioButtonUnchecked fontSize="small" color="disabled" />
                    <Typography variant="subtitle2" sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Unassigned
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {unassignedForms.length} form{unassignedForms.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>
                {unassignedForms.map((group) => renderFormItem(group, true))}
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                    Match forms to individuals, businesses, and trusts
                </Typography>
            </Box>

            {/* Link to parent menu */}
            <Menu
                anchorEl={linkMenuAnchor?.element}
                open={Boolean(linkMenuAnchor)}
                onClose={() => setLinkMenuAnchor(null)}
            >
                {linkMenuAnchor && getPotentialParents(linkMenuAnchor.formId).map((parentGroup) => (
                    <MenuItem
                        key={parentGroup.formInstanceId}
                        onClick={() => {
                            handleAssignFormToParent(linkMenuAnchor.formId, parentGroup.formInstanceId);
                            setLinkMenuAnchor(null);
                        }}
                    >
                        <AccountTree fontSize="small" sx={{ mr: 1 }} />
                        {parentGroup.formType}
                        {parentGroup.name && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                {parentGroup.name}
                            </Typography>
                        )}
                        {matchData.formAssignments[parentGroup.formInstanceId]?.assignedTo && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({matchData.formAssignments[parentGroup.formInstanceId].assignedTo?.name})
                            </Typography>
                        )}
                    </MenuItem>
                ))}
                {linkMenuAnchor && getPotentialParents(linkMenuAnchor.formId).length === 0 && (
                    <MenuItem disabled>
                        <Typography variant="caption" color="text.secondary">
                            No eligible parent forms in this tax year
                        </Typography>
                    </MenuItem>
                )}
            </Menu>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {taxYears.map((taxYear) => (
                    <Accordion
                        key={taxYear}
                        expanded={expandedTaxYear === taxYear}
                        onChange={(_, isExpanded) => setExpandedTaxYear(isExpanded ? taxYear : false)}
                        sx={{ mb: 1 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    Tax Year: {taxYear}
                                </Typography>
                                <Chip label={`${getGroupsForTaxYear(taxYear).length} forms`} size="small" />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            {allEntities.map((entity) => renderEntitySection(taxYear, entity))}
                            {renderUnassignedSection(taxYear)}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            {/* Actions */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBack />}
                    onClick={onBack}
                >
                    Back to Groups
                </Button>
                <Button
                    variant="outlined"
                    startIcon={<Undo />}
                    onClick={handleUndo}
                    disabled={undoHistory.length === 0}
                >
                    Undo ({undoHistory.length})
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" onClick={handleSaveDraft} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>
                <Button variant="outlined" color="warning" onClick={handleKickbackClick} disabled={isSaving}>
                    Send back to Extract
                </Button>
                <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isSaving}>
                    {isSaving ? 'Submitting...' : 'Submit'}
                </Button>
            </Box>

            {/* Unassigned forms confirmation dialog */}
            <Dialog open={showUnassignedDialog} onClose={() => setShowUnassignedDialog(false)}>
                <DialogTitle>Unassigned Forms</DialogTitle>
                <DialogContent>
                    <Typography>
                        There {unassignedCount === 1 ? 'is' : 'are'} still{' '}
                        <strong>{unassignedCount}</strong> unassigned form{unassignedCount !== 1 ? 's' : ''}.
                        Are you sure you want to submit?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowUnassignedDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setShowUnassignedDialog(false);
                            doSubmit();
                        }}
                    >
                        Submit Anyway
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Kickback dialog */}
            <Dialog
                open={showKickbackDialog}
                onClose={() => !kickbackSubmitting && setShowKickbackDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Send back to Extract</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This task will be moved back to the Extract queue for re-entry. Any unsaved
                        grouping changes will be discarded. A reason is required.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={6}
                        label="Reason"
                        value={kickbackReason}
                        onChange={(e) => setKickbackReason(e.target.value)}
                        disabled={kickbackSubmitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowKickbackDialog(false)} disabled={kickbackSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleKickbackConfirm}
                        disabled={!kickbackReason.trim() || kickbackSubmitting}
                    >
                        Send back
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FormEntityAssigner;
